import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Avatar,
  Collapse,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import {
  Dashboard as DashboardIcon,
  Videocam as CameraIcon,
  Today as DailyAttendanceIcon,
  Timeline as ChildTimelineIcon,
  Person as PersonIcon,
  School as TeacherIcon,
  FamilyRestroom as ParentIcon,
  Business as ClassroomIcon,
  EventNote as ScheduleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Devices as EdgeDeviceIcon,
  BeachAccess as VacationIcon,
  Event as EventIcon,
  Policy as PolicyIcon,
  Notifications as NotificationsIcon,
  Lock as LockIcon,
  Build as BuildIcon,
  Collections as CollectionsIcon,
  Payment as PaymentIcon,
  AccountBalanceWallet as PayoutIcon,
  FactCheck as ConsentIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import LittleLensLogo from './LittleLensLogo';
import dataMindLogo from '../assets/DataMind_Logo.png';
import NurserySelector from './NurserySelector';
import { presentationBackground } from '../theme/presentationBackground';

const drawerWidth = 280;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { user, logout, currentNursery, nurseries } = useAuth();
  const { 
    canViewDashboard, 
    canManageCameras, 
    canManageChildren, 
    canManageTeachers,
    canManageParents,
    canManageClassrooms, 
    canManageSchedules, 
    canViewReports, 
    canManageEdgeDevices,
    canManageVacations,
    canManagePolicies,
    canAccessSettings,
    canManageRoles,
    canAccessUserManagement,
    canAssignUsers,
    canAccessNurseryManagement,
    canAccessHierarchy,
    canViewCustomReports,
    canManageHolidays,
    canViewEvents,
    canViewSnapshots,
    canAccessSnapshotSettings,
    canAccessChildImageDisplaySettings,
    canManageBilling,
    canManageConsent,
    isSuperAdmin
  } = useRolePermissions();

  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [billingOpen, setBillingOpen] = React.useState(false);
  const [visionIQOpen, setVisionIQOpen] = React.useState(false);

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSettingsOpen(!settingsOpen);
  };

  const handleBillingClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setBillingOpen(!billingOpen);
  };

  const handleVisionIQClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVisionIQOpen(!visionIQOpen);
  };

  React.useEffect(() => {
    const settingsPaths = [
      'settings/hierarchy',
      'settings/roles', 
      'settings/users', 
      'settings/assignments', 
      'settings/nurseries', 
      'settings/child-image-display-settings',
      'settings/groups',
      'settings/locations'
    ];
    const isSettingsSubPage = settingsPaths.some(path => location.pathname.includes(path));
    if (isSettingsSubPage) {
      setSettingsOpen(true);
    }

    const visionIQPaths = ['snapshots', 'settings/snapshot-settings', 'settings/analysis-prompts'];
    const isVisionIQSubPage = visionIQPaths.some(path => location.pathname.includes(path));
    if (isVisionIQSubPage) {
      setVisionIQOpen(true);
    }

    const billingPaths = ['subscriptions', 'payouts'];
    const isBillingSubPage = billingPaths.some(path => location.pathname.includes(path));
    if (isBillingSubPage) {
      setBillingOpen(true);
    }
  }, [location.pathname]);

  const menuItemsAboveVisionIQ = [
    { text: t('nav.dashboard'), icon: <DashboardIcon />, path: 'dashboard', canAccess: canViewDashboard },
    { text: t('nav.edgeDevices'), icon: <EdgeDeviceIcon />, path: 'edge-devices', canAccess: canManageEdgeDevices },
    { text: t('nav.cameras'), icon: <CameraIcon />, path: 'cameras', canAccess: canManageCameras },
  ];

  const menuItemsBelowVisionIQ = [
    { text: t('nav.events'), icon: <EventIcon />, path: 'events', canAccess: canViewEvents },
    { text: t('nav.schedules'), icon: <ScheduleIcon />, path: 'schedules', canAccess: canManageSchedules },
    { text: t('nav.classrooms'), icon: <ClassroomIcon />, path: 'classrooms', canAccess: canManageClassrooms },
    { text: t('nav.children'), icon: <PersonIcon />, path: 'children', canAccess: canManageChildren },
    { text: t('nav.teachers'), icon: <TeacherIcon />, path: 'teachers', canAccess: canManageTeachers },
    { text: t('nav.parents'), icon: <ParentIcon />, path: 'parents', canAccess: canManageParents },
    { text: t('nav.consentForms'), icon: <ConsentIcon />, path: 'consent-forms', canAccess: canManageConsent },
    { text: t('nav.customReports'), icon: <BuildIcon />, path: 'custom-reports', canAccess: canViewCustomReports },
    { text: t('nav.vacations'), icon: <VacationIcon />, path: 'vacations', canAccess: canManageVacations },
    { text: t('nav.holidays'), icon: <EventIcon />, path: 'holidays', canAccess: canManageHolidays },
    { text: t('nav.dailyAttendance'), icon: <DailyAttendanceIcon />, path: 'daily-attendance', canAccess: canViewReports },
    { text: t('nav.childTimeline'), icon: <ChildTimelineIcon />, path: 'child-timeline', canAccess: canViewReports },
    { text: t('nav.policies'), icon: <PolicyIcon />, path: 'policies', canAccess: canManagePolicies },
    { text: t('nav.notifications'), icon: <NotificationsIcon />, path: 'notifications', canAccess: canViewReports },
  ];

  const settingsSubItems = [
    { text: t('nav.nurseryManagement'), path: 'settings/nurseries', canAccess: canAccessNurseryManagement },
    { text: t('nav.organizationHierarchy'), path: 'settings/hierarchy', canAccess: canAccessHierarchy },
    { text: t('nav.roleManagement'), path: 'settings/roles', canAccess: canManageRoles },
    { text: t('nav.userManagement'), path: 'settings/users', canAccess: canAccessUserManagement },
    { text: t('nav.userNurseryAssignments'), path: 'settings/assignments', canAccess: canAssignUsers },
    { text: t('nav.childImageDisplaySettings'), path: 'settings/child-image-display-settings', canAccess: canAccessChildImageDisplaySettings },
  ];

  const visionIQSubItems = [
    { text: t('nav.snapshots'), path: 'snapshots', canAccess: canViewSnapshots },
    { text: t('nav.snapshotSettings'), path: 'settings/snapshot-settings', canAccess: canAccessSnapshotSettings },
    { text: t('nav.analysisPrompts'), path: 'settings/analysis-prompts', canAccess: isSuperAdmin },
  ];

  const billingSubItems = [
    { text: t('nav.subscriptions'), path: 'subscriptions', canAccess: canManageBilling },
    { text: t('nav.payouts'), path: 'payouts', canAccess: canManageBilling },
  ];

  const canAccessVisionIQMenu =
    canViewSnapshots || canAccessSnapshotSettings || isSuperAdmin;

  const canShowVisionIQMenu =
    canAccessVisionIQMenu && !(!currentNursery && nurseries.length === 0);

  const isVisionIQPathActive =
    location.pathname.includes('/snapshots') ||
    location.pathname.includes('/settings/snapshot-settings') ||
    location.pathname.includes('/settings/analysis-prompts');

  const isSettingsPathActive =
    location.pathname.includes('/settings') && !isVisionIQPathActive;

  // Filter menu items based on permissions
  const getAvailableMenuItems = (items: typeof menuItemsAboveVisionIQ) => {
    const filteredMenuItems = items.filter(item => item.canAccess);

    // Filter menu items for users with no nurseries
    return !currentNursery && nurseries.length === 0
      ? filteredMenuItems.filter(item => {
          // Only show Dashboard for users with dashboard permissions
          if (item.path === 'dashboard') return canViewDashboard;
          return false;
        })
      : filteredMenuItems;
  };

  const availableMenuItemsAboveVisionIQ = getAvailableMenuItems(menuItemsAboveVisionIQ);
  const availableMenuItemsBelowVisionIQ = getAvailableMenuItems(menuItemsBelowVisionIQ);

  const handleNavigation = (path: string) => {
    if (currentNursery) {
      navigate(`/nursery/${currentNursery.nursery_id}/${path}`);
    } else if (nurseries.length === 0) {
      // User has no nurseries
      if (path === 'dashboard' && canViewDashboard) {
        navigate(`/no-nursery/${path}`);
      } else if (
        (path === 'settings' || path.startsWith('settings/')) &&
        canAccessSettings
      ) {
        navigate(`/${path}`);
      }
    } else {
      // User has nurseries but none selected - redirect to first nursery
      const firstNursery = nurseries[0];
      navigate(`/nursery/${firstNursery.nursery_id}/${path}`);
    }
    onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper function to check if a menu item is currently active
  const isMenuItemActive = (menuPath: string) => {
    if (currentNursery) {
      const expectedPath = `/nursery/${currentNursery.nursery_id}/${menuPath}`;
      return location.pathname === expectedPath;
    } else if (nurseries.length === 0) {
      // Check no-nursery routes
      if (menuPath === 'dashboard' && canViewDashboard) {
        const expectedPath = `/no-nursery/${menuPath}`;
        return location.pathname === expectedPath;
      } else if (
        (menuPath === 'settings' || menuPath.startsWith('settings/')) &&
        canAccessSettings
      ) {
        return location.pathname === `/${menuPath}`;
      }
    }
    return false;
  };

  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#ffffff' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          pb: 2,
          background: presentationBackground.sidebarGradient,
          color: 'white',
        }}
      >
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
          <LittleLensLogo
            alt={t('branding.littleLensLogoAlt')}
            maxWidth={100}
          />
        </Box>
        
        {/* DataMind Branding */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-end',
            mb: 1.5,
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.7rem',
              fontWeight: 400,
              mr: 0.5,
              color: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            {t('branding.byDataMind')}
          </Typography>
          <img 
            src={dataMindLogo} 
            alt="DataMind Logo" 
            style={{
              height: '32px',
              width: 'auto',
              opacity: 0.9,
            }}
          />
        </Box>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: 'rgba(255,255,255,0.12)',
          backdropFilter: 'blur(8px)',
        }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              mr: 1.5,
              bgcolor: '#ffffff',
              color: presentationBackground.primaryDark,
              fontSize: '0.95rem',
              fontWeight: 700,
            }}
          >
            {user?.name?.charAt(0).toUpperCase() ?? '?'}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: 'white', lineHeight: 1.3 }}>
              {user?.name}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem' }}>
              {user?.email}
            </Typography>
          </Box>
        </Box>
        
        {/* Nursery Selector */}
        <Box sx={{ mt: 1.5 }}>
          <NurserySelector />
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, py: 1.5, overflowY: 'auto' }}>
        <List sx={{ px: 1.5 }}>
          {availableMenuItemsAboveVisionIQ.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isMenuItemActive(item.path)}
                disabled={!currentNursery && nurseries.length > 0}
                sx={{
                  py: 1,
                  '&.Mui-disabled': {
                    opacity: 0.45,
                  },
                }}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isMenuItemActive(item.path) ? 700 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

          {canShowVisionIQMenu && (
            <>
              <ListItem disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={handleVisionIQClick}
                  selected={isVisionIQPathActive}
                  disabled={!currentNursery && nurseries.length > 0}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon>
                    <CollectionsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.visionIQ')}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isVisionIQPathActive ? 700 : 500,
                    }}
                  />
                  {visionIQOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={visionIQOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 3 }}>
                  {visionIQSubItems.filter(item => item.canAccess).map((subItem) => (
                    <ListItem key={subItem.text} disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(subItem.path)}
                        selected={isMenuItemActive(subItem.path)}
                        sx={{ py: 0.75 }}
                      >
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontSize: '0.825rem',
                            fontWeight: isMenuItemActive(subItem.path) ? 700 : 500,
                            color: isMenuItemActive(subItem.path) ? 'white' : 'inherit',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {availableMenuItemsBelowVisionIQ.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isMenuItemActive(item.path)}
                disabled={!currentNursery && nurseries.length > 0}
                sx={{
                  py: 1,
                  '&.Mui-disabled': {
                    opacity: 0.45,
                  },
                }}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isMenuItemActive(item.path) ? 700 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

          {canManageBilling && currentNursery && (
            <>
              <ListItem disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={handleBillingClick}
                  selected={location.pathname.includes('/subscriptions') || location.pathname.includes('/payouts')}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon>
                    <PaymentIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.billing')}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: (location.pathname.includes('/subscriptions') || location.pathname.includes('/payouts')) ? 700 : 500,
                    }}
                  />
                  {billingOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={billingOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 3 }}>
                  {billingSubItems.filter(item => item.canAccess).map((subItem) => (
                    <ListItem key={subItem.text} disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(subItem.path)}
                        selected={isMenuItemActive(subItem.path)}
                        sx={{ py: 0.75 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {subItem.path === 'payouts' ? <PayoutIcon fontSize="small" /> : <PaymentIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontSize: '0.825rem',
                            fontWeight: isMenuItemActive(subItem.path) ? 700 : 500,
                            color: isMenuItemActive(subItem.path) ? 'white' : 'inherit',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {/* Settings Collapsible Item */}
          {canAccessSettings && (
            <>
              <ListItem disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={handleSettingsClick}
                  selected={isSettingsPathActive}
                  disabled={!currentNursery && nurseries.length > 0}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('nav.settings')}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isSettingsPathActive ? 700 : 500,
                    }}
                  />
                  {settingsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 3 }}>
                  {settingsSubItems.filter(item => item.canAccess).map((subItem) => (
                    <ListItem key={subItem.text} disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(subItem.path)}
                        selected={isMenuItemActive(subItem.path)}
                        sx={{ py: 0.75 }}
                      >
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontSize: '0.825rem',
                            fontWeight: isMenuItemActive(subItem.path) ? 700 : 500,
                            color: isMenuItemActive(subItem.path) ? 'white' : 'inherit',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </List>
      </Box>

      {/* Footer Actions */}
      <Box sx={{ p: 1.5, pt: 0 }}>
        <Divider sx={{ mb: 1.5 }} />
        <ListItemButton
          onClick={() => {
            navigate('/account');
            onClose();
          }}
          selected={location.pathname === '/account'}
          sx={{ mb: 0.5, py: 1 }}
        >
          <ListItemIcon>
            <LockIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('nav.changePassword')}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: location.pathname === '/account' ? 700 : 500,
            }}
          />
        </ListItemButton>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            py: 1,
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'rgba(239,68,68,0.06)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('nav.logout')}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: '0 4px 24px rgba(15,23,42,0.06)',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
